import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { newChoiceId, type FormField } from "@koaform/form-schema";
import { RatingIcon } from "@koaform/ui";
import {
  Bars3Icon,
  DocumentDuplicateIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { isLayout } from "./fieldDefaults.js";

type Props = {
  fields: FormField[];
  selectedId: string | null;
  /** True while a palette block is being dragged over the canvas. */
  inserting: boolean;
  onSelect: (id: string) => void;
  onChange: (field: FormField) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
};

/**
 * The append target for a palette drag — doubles as the empty state and the
 * "pick a block" hint. Only one instance exists at a time, so the droppable id
 * stays unique.
 */
export const CANVAS_APPEND_ID = "canvas-append";

export const DropHint = ({
  inserting,
  children,
}: {
  inserting: boolean;
  children: React.ReactNode;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_APPEND_ID });
  return (
    <p
      ref={setNodeRef}
      className={`rounded-[14px] border-[1.5px] border-dashed p-5 text-center text-sm transition-colors ${
        isOver && inserting
          ? "border-primary-600 bg-primary-50 text-primary-600"
          : "border-line-bold text-faint"
      }`}
    >
      {inserting ? "Drop to add it here" : children}
    </p>
  );
};

const iconBtn =
  "flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] border border-line-strong bg-surface text-faint";

/**
 * Stops a pointer press reaching the card's drag listeners.
 *
 * The whole card is draggable, which is what people expect — but a press that
 * lands on a text input has to put the caret there, not start a drag.
 */
const notDraggable = {
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
};

/**
 * The body of a card: a sketch of the answer control, with the parts worth
 * editing in place actually editable.
 *
 * Choice labels are the case that matters. They were read-only here, so
 * renaming an option meant selecting the block and going to the side panel —
 * for the one piece of text most likely to need a change.
 */
const Body = ({
  field,
  onChange,
}: {
  field: FormField;
  onChange: (field: FormField) => void;
}) => {
  const patch = (changes: Partial<FormField>) =>
    onChange({ ...field, ...changes } as FormField);

  switch (field.type) {
    case "heading":
    case "paragraph":
      return null; // its text IS the editable title above

    case "long_text":
      return (
        <div className="h-[76px] rounded-[10px] border border-line-input bg-subtle px-[13px] py-3 text-sm text-faint">
          {field.placeholder ?? "Take as much space as you need"}
        </div>
      );

    case "multiple_choice":
    case "checkboxes":
    case "dropdown": {
      const marker =
        field.type === "checkboxes"
          ? "rounded-[5px]"
          : field.type === "dropdown"
            ? "rounded-[3px]"
            : "rounded-full";
      return (
        <div className="flex flex-col gap-1.5">
          {field.choices.map((choice, i) => (
            <div
              key={choice.id}
              className="group/choice flex items-center gap-2.5"
            >
              <span
                className={`h-4 w-4 flex-none border-[1.5px] border-line-bold ${marker}`}
              />
              <input
                {...notDraggable}
                aria-label={`Option ${i + 1}`}
                value={choice.label}
                placeholder={`Option ${i + 1}`}
                onChange={(e) =>
                  patch({
                    choices: field.choices.map((c) =>
                      c.id === choice.id ? { ...c, label: e.target.value } : c,
                    ),
                  } as Partial<FormField>)
                }
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  patch({
                    // A fresh id every time — reusing one would repoint old
                    // responses at a different answer.
                    choices: [
                      ...field.choices,
                      { id: newChoiceId(), label: "" },
                    ],
                  } as Partial<FormField>);
                }}
                className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-sm text-body placeholder-faint hover:border-line-strong focus:border-primary-600 focus:bg-subtle focus:outline-none"
              />
              <button
                {...notDraggable}
                type="button"
                aria-label={`Remove option ${i + 1}`}
                disabled={field.choices.length <= 1}
                onClick={() =>
                  patch({
                    choices: field.choices.filter((c) => c.id !== choice.id),
                  } as Partial<FormField>)
                }
                className="flex h-6 w-6 flex-none items-center justify-center rounded-md text-faint opacity-0 group-hover/choice:opacity-100 hover:text-danger focus-visible:opacity-100 disabled:opacity-0"
              >
                <XMarkIcon className="size-3.5" />
              </button>
            </div>
          ))}

          {"allowOther" in field && field.allowOther ? (
            <div className="flex items-center gap-2.5 text-sm text-faint">
              <span
                className={`h-4 w-4 flex-none border-[1.5px] border-line-bold ${marker}`}
              />
              <span className="px-1.5">Other…</span>
            </div>
          ) : null}

          <button
            {...notDraggable}
            type="button"
            onClick={() =>
              patch({
                choices: [...field.choices, { id: newChoiceId(), label: "" }],
              } as Partial<FormField>)
            }
            className="mt-0.5 flex items-center gap-1.5 self-start rounded-md px-1.5 py-0.5 text-[13px] font-semibold text-primary-600 hover:bg-primary-50"
          >
            <PlusIcon className="size-3.5" /> Add option
          </button>
        </div>
      );
    }

    case "rating":
      return (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: field.max }, (_, i) => i + 1).map((n) =>
            field.shape === "number" ? (
              <span
                key={n}
                className="grid h-9 w-9 place-items-center rounded-lg border-[1.5px] border-line-input text-[15px] font-bold text-body"
              >
                {n}
              </span>
            ) : (
              <span
                key={n}
                className="grid h-8 w-8 place-items-center text-line-bold"
              >
                <RatingIcon shape={field.shape} className="h-7 w-7" />
              </span>
            ),
          )}
        </div>
      );

    case "file_upload":
      return (
        <div className="rounded-[10px] border border-dashed border-line-bold bg-subtle px-3 py-4 text-center text-sm text-faint">
          Up to {field.maxSizeMb} MB
        </div>
      );

    default:
      return (
        <div className="flex h-[42px] items-center rounded-[10px] border border-line-input bg-subtle px-[13px] text-sm text-faint">
          {"placeholder" in field && field.placeholder
            ? field.placeholder
            : "Type your answer"}
        </div>
      );
  }
};

const Row = ({
  field,
  selected,
  inserting,
  onSelect,
  onChange,
  onDelete,
  onDuplicate,
}: {
  field: FormField;
  selected: boolean;
  inserting: boolean;
  onSelect: () => void;
  onChange: (field: FormField) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = // prettier-ignore
    useSortable({ id: field.id });

  const layout = isLayout(field.type);
  const titleKey = layout ? "text" : "label";
  const title = (field as unknown as Record<string, string>)[titleKey] ?? "";

  const patch = (changes: Partial<FormField>) =>
    onChange({ ...field, ...changes } as FormField);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      // Stopped so the canvas's own click — which DEselects — never fires
      // from a click that landed on a card.
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      /**
       * Drag handlers live on the CARD, not on a grip icon. Grabbing a card
       * anywhere is what people try first; hunting for a handle is a step
       * nobody asks for. The 4px activation distance keeps plain clicks
       * working, and inputs stop propagation so typing still puts the caret
       * where you pressed.
       */
      {...attributes}
      {...listeners}
      className={`group cursor-grab rounded-[14px] border-2 bg-surface px-5 py-[18px] active:cursor-grabbing ${
        selected
          ? "border-primary-600 shadow-[0_0_0_4px_rgba(29,107,229,0.10)]"
          : "border-transparent hover:border-line-strong"
      } ${isDragging ? "opacity-60 shadow-lg" : ""} ${
        // A palette block dropped here lands BEFORE this card — say so.
        inserting && isOver
          ? "shadow-[0_-3px_0_0_var(--color-primary-600)]"
          : ""
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Click straight into the question — no trip to the side panel.
              Required shows as the asterisk respondents will see, rather than
              as a caption in builder-only language. */}
          <div className="flex items-baseline">
            <input
              {...notDraggable}
              aria-label={layout ? "Text" : "Question"}
              value={title}
              placeholder={layout ? "Section heading" : "Your question"}
              onChange={(e) => patch({ [titleKey]: e.target.value } as Partial<FormField>)} // prettier-ignore
              className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-[15.5px] font-bold tracking-[-0.01em] text-ink placeholder-faint hover:border-line-strong focus:border-primary-600 focus:bg-subtle focus:outline-none"
            />
            {"required" in field && field.required ? (
              <span
                title="Required"
                className="flex-none pr-1 text-[15.5px] font-bold text-danger"
              >
                *
              </span>
            ) : null}
          </div>
          {layout ? null : (
            <input
              {...notDraggable}
              aria-label="Helper text"
              value={"description" in field ? (field.description ?? "") : ""}
              placeholder="Add helper text"
              onChange={(e) =>
                patch({
                  description: e.target.value || undefined,
                } as Partial<FormField>)
              }
              className="mt-0.5 w-full rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-[13px] text-faint placeholder-faint hover:border-line-strong focus:border-primary-600 focus:bg-subtle focus:outline-none"
            />
          )}
        </div>

        <div className="flex flex-none items-center gap-1" {...notDraggable}>
          <span
            aria-hidden="true"
            title="Drag anywhere on this card to reorder"
            className={`${iconBtn} border-transparent opacity-0 group-hover:opacity-100`}
          >
            <Bars3Icon className="size-3.5" />
          </span>
          <button
            type="button"
            onClick={onDuplicate}
            aria-label="Duplicate"
            className={`${iconBtn} opacity-0 hover:border-primary-600 hover:text-primary-600 focus-visible:opacity-100 group-hover:opacity-100`}
          >
            <DocumentDuplicateIcon className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete"
            className={`${iconBtn} opacity-0 hover:border-danger hover:text-danger focus-visible:opacity-100 group-hover:opacity-100`}
          >
            <TrashIcon className="size-3.5" />
          </button>
        </div>
      </div>

      {/* No type caption under the card: the control's own shape already says
          what it is, the side panel says it again, and the line cost a row of
          vertical space on every block. */}
      <div {...notDraggable}>
        <Body field={field} onChange={onChange} />
      </div>
    </li>
  );
};

/**
 * The canvas. The DndContext lives in Builder — one context spans the palette
 * and this list, which is what lets a block be dragged straight off the
 * sidebar and dropped between two questions.
 */
export const FieldList = ({
  fields,
  selectedId,
  inserting,
  onSelect,
  onChange,
  onDelete,
  onDuplicate,
}: Props) => {
  if (fields.length === 0) {
    return (
      <DropHint inserting={inserting}>
        <span className="lg:hidden">Tap “+ Add block” below</span>
        <span className="hidden lg:inline">
          Pick a block on the left — or drag one here
        </span>
      </DropHint>
    );
  }

  return (
    <SortableContext
      items={fields.map((f) => f.id)}
      strategy={verticalListSortingStrategy}
    >
      <ul className="flex flex-col gap-3">
        {fields.map((field) => (
          <Row
            key={field.id}
            field={field}
            selected={field.id === selectedId}
            inserting={inserting}
            onSelect={() => onSelect(field.id)}
            onChange={onChange}
            onDelete={() => onDelete(field.id)}
            onDuplicate={() => onDuplicate(field.id)}
          />
        ))}
      </ul>
    </SortableContext>
  );
};
